import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Img from 'gatsby-image';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Section = styled.section`
  background: #fff;
  padding: 100px 0 80px;

  @media (max-width: 768px) {
    padding: 60px 0 48px;
  }
`;

const Container = styled.div`
  /* Narrower than the other sections so the comparison image reads as a
     framed panel instead of a full-bleed banner */
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 28px;
  }
`;

const TitleBlock = styled.div`
  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: '';
      width: 24px;
      height: 1px;
      background: #f08020;
    }
  }

  h2 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NavBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid #d8e1ec;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
    stroke: #1b2733;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    border-color: #f08020;
    background: #f08020;
    svg {
      stroke: #fff;
    }
  }
`;

const CompareContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  overflow: hidden;
  cursor: col-resize;
  user-select: none;
  animation: ${fadeIn} 0.5s ease;

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
  }

  @media (max-width: 640px) {
    aspect-ratio: 4 / 3;
  }
`;

const ImageLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    pointer-events: none;
  }
`;

const ClipLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  clip-path: ${({ clipPos }) => `inset(0 ${100 - clipPos}% 0 0)`};

  img,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    pointer-events: none;
  }
`;

const DragLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ pos }) => pos}%;
  transform: translateX(-50%);
  width: 2px;
  background: #f08020;
  z-index: 10;
  pointer-events: none;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ pos }) => pos}%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f08020;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11;
  cursor: col-resize;
  box-shadow: 0 2px 16px rgba(11, 93, 184, 0.25);

  svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #fff;
    stroke-width: 2;
    stroke-linecap: round;
  }

  @media (max-width: 640px) {
    width: 34px;
    height: 34px;
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const Label = styled.span`
  position: absolute;
  bottom: 16px;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  z-index: 5;

  &.label-before {
    left: 16px;
  }
  &.label-after {
    right: 16px;
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const ProjectTitle = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #5b6b7c;
`;

const Counter = styled.span`
  font-size: 12px;
  color: #9fb0c3;
  letter-spacing: 0.05em;

  .cur {
    color: #1378e5;
    font-weight: 600;
  }
`;

export default function BeforeAfter({ tag, title, items }) {
  const [current, setCurrent] = useState(0);
  const [clipPos, setClipPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const data = items || [];

  const goNext = useCallback(() => {
    setCurrent(prev => (prev + 1) % data.length);
    setClipPos(50);
  }, [data.length]);

  const goPrev = useCallback(() => {
    setCurrent(prev => (prev - 1 + data.length) % data.length);
    setClipPos(50);
  }, [data.length]);

  useEffect(() => {
    if (data.length <= 1) return;
    if (isDragging.current) return;
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, [goNext, data.length, current]);

  const getPosition = useCallback(e => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 50;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    return Math.max(5, Math.min(95, (x / rect.width) * 100));
  }, []);

  const onStart = useCallback(
    e => {
      isDragging.current = true;
      setClipPos(getPosition(e));
    },
    [getPosition]
  );

  const onMove = useCallback(
    e => {
      if (!isDragging.current) return;
      e.preventDefault();
      setClipPos(getPosition(e));
    },
    [getPosition]
  );

  const onEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [onMove, onEnd]);

  if (!data.length) return null;

  const item = data[current];
  const beforeFluid = item.beforeImage?.asset?.fluid;
  const beforeUrl = item.beforeImage?.asset?.url;
  const afterFluid = item.afterImage?.asset?.fluid;
  const afterUrl = item.afterImage?.asset?.url;

  return (
    <Section>
      <Container>
        <TopRow>
          <TitleBlock>
            {tag && <div className="tag">{tag}</div>}
            <h2>{title || 'Before & After'}</h2>
          </TitleBlock>
          {data.length > 1 && (
            <NavRow>
              <NavBtn onClick={goPrev} aria-label="Previous">
                <svg viewBox="0 0 24 24">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </NavBtn>
              <NavBtn onClick={goNext} aria-label="Next">
                <svg viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </NavBtn>
            </NavRow>
          )}
        </TopRow>

        <CompareContainer
          key={current}
          ref={containerRef}
          onMouseDown={onStart}
          onTouchStart={onStart}
        >
          <ImageLayer>
            {afterFluid ? (
              <Img
                fluid={afterFluid}
                style={{ height: '100%' }}
                imgStyle={{ objectFit: 'cover' }}
                alt="After"
              />
            ) : afterUrl ? (
              <img src={afterUrl} alt="After" />
            ) : null}
          </ImageLayer>

          <ClipLayer clipPos={clipPos}>
            {beforeFluid ? (
              <Img
                fluid={beforeFluid}
                style={{ height: '100%' }}
                imgStyle={{ objectFit: 'cover' }}
                alt="Before"
              />
            ) : beforeUrl ? (
              <img src={beforeUrl} alt="Before" />
            ) : null}
          </ClipLayer>

          <Label className="label-before">Before</Label>
          <Label className="label-after">After</Label>
          <DragLine pos={clipPos} />
          <DragHandle pos={clipPos}>
            <svg viewBox="0 0 24 24">
              <path d="M8 18l-6-6 6-6" />
              <path d="M16 6l6 6-6 6" />
            </svg>
          </DragHandle>
        </CompareContainer>

        <BottomRow>
          <ProjectTitle>{item.title || ''}</ProjectTitle>
          <Counter>
            <span className="cur">{String(current + 1).padStart(2, '0')}</span>
            {' / '}
            {String(data.length).padStart(2, '0')}
          </Counter>
        </BottomRow>
      </Container>
    </Section>
  );
}
